import { TOURNAMENTS, clubId, dayAdd, runMember } from './tournaments.mjs';
/**
 * The competition index (2026-10-23): one lean record per tournament, league, tour or show page the build wrote
 * (src/data/tournaments/built/_index.json, scripts/gen-tournaments.mjs). The homepage row, the menus and the list
 * pages' cross-links read it, so they point only at pages that exist in THIS build, and never at one the index
 * is not allowed to have (`idx`), and the homepage never features one the legal switches hold back (`promoted`).
 * Pure: the build, the site and the tests share it; nothing here reads a file or a clock (callers pass `today`),
 * so the prerender and the first browser render choose the same pages.
 *
 * @typedef {{ id: string, title: string, short: string, kind: string, group: 'sports' | 'theatre' | 'music', from: string, until: string,
 *   idx: 0 | 1, promoted: 0 | 1, place: string, price: number | null, generated: string, clubs?: string[] }} CompEntry
 * The main bundle carries only the slim files below, never the full index (2026-10-23): the menus' lists, the homepage's candidates, the cross-links' entries.
 * @typedef {Pick<CompEntry, 'id' | 'title' | 'kind' | 'from' | 'until' | 'idx' | 'promoted' | 'place' | 'price'> & Partial<Pick<CompEntry, 'group' | 'short' | 'generated' | 'clubs'>>} CompSlim
 */

/** The season guide of a league list page (/league/<slug>). */
export const SEASON_HUB = {
  mls: 'mls-2026',
  nba: 'nba-2026-27',
  nhl: 'nhl-2026-27',
  nfl: 'nfl-2026',
  ncaa_basketball: 'ncaab-2026-27',
};

/** A venue name short enough for a tile ("Alexandra Palace" yes, "Arenas in the UK, Ireland, Germany ..." no). @param {string} venue */
const shortPlace = (venue) => {
  const v = String(venue ?? '').replace(/ \(.*\)/, '').trim();
  return v.length <= 32 && !/,/.test(v) ? v : '';
};

/**
 * The index record of one built page (the object gen-tournaments.mjs writes to <id>.json).
 * `promoted`: indexable AND no game of a league held out by the legal switches (Italy and France football are
 * listed but never promoted: shared/legal.mjs notPromoted), so a league with held games is never featured.
 * @param {any} built @returns {CompEntry}
 */
export const indexEntry = (built) => {
  const run = built.type === 'run';
  const sub = run ? built.sub : null;
  return {
    id: built.id,
    title: run ? (sub === 'tour' ? `${built.short} tour` : sub === 'show' ? built.name : built.short) : built.name,
    short: built.short,
    kind: run ? 'run' : built.kind,
    group: run ? (built.category === 'Theater' ? 'theatre' : 'music') : 'sports',
    from: built.window.from,
    until: built.window.until,
    idx: built.idx ? 1 : 0,
    promoted: built.idx && !(built.counts?.held > 0) ? 1 : 0,
    place: run ? '' : shortPlace(built.city || built.venue),
    price: typeof built.fromAllIn === 'number' && built.fromAllIn > 0 ? built.fromAllIn : null,
    generated: built.generated,
    ...(built.kind === 'league' && built.league?.clubs ? { clubs: built.league.clubs.map((c) => c.id) } : {}),
  };
};

/** Up to `max` titles per group, as the menus print them (built/_menu.json). @param {CompEntry[]} index */
export const menuFile = (index, max = 5) =>
  Object.fromEntries(['sports', 'theatre', 'music'].map((g) => [g, menuCompetitions(index, /** @type {'sports' | 'theatre' | 'music'} */ (g), max).map((e) => ({ id: e.id, title: e.title }))]));

// The two files below are rows of arrays, and a page's title and kind are read back from the registry (shared/tournaments.mjs, already in the
// bundle) instead of being written again: every key and title spelled out ~90 times was most of the bytes. Rows are always indexable and
// promoted (the filters below), so those two flags are not written; unpack* puts them back.
const BY_ID = new Map(TOURNAMENTS.map((t) => [t.id, t]));
/** The title a menu, tile or link prints for a registry page (what indexEntry writes). @param {any} t */
const registryTitle = (t) => (t.run ? (t.run.sub === 'tour' ? `${t.short} tour` : t.run.sub === 'show' ? t.name : t.short) : t.name);

/**
 * What the homepage row can choose from (built/_featured.json): [id, from, until, place, price] for the pages the row would pick within
 * a fortnight of this build (twelve at most), plus `keep` (the editors' own ids, src/data/featured.ts) when indexable and promoted.
 * @param {CompEntry[]} index @param {string[]} [keep]
 */
export const featuredFile = (index, keep = []) => {
  const day = indexDay(index);
  const ok = index.filter((e) => e.idx && e.promoted && e.until >= day);
  const rows = new Map(liveCompetitions(ok, day, { days: 45, max: 12 }).map((e) => [e.id, e]));
  for (const e of ok) if (keep.includes(e.id)) rows.set(e.id, e);
  return [...rows.values()].map((e) => [e.id, e.from, e.until, e.place, e.price]);
};
/** @param {any[][]} rows @returns {CompSlim[]} */
export const unpackFeatured = (rows) =>
  rows.flatMap(([id, from, until, place, price]) => {
    const t = BY_ID.get(id);
    return t ? [{ id, title: registryTitle(t), kind: t.run ? 'run' : t.kind, from, until, idx: 1, promoted: 1, place, price }] : [];
  });

/**
 * What the cross-links can choose from: promoted pages not over, club ids for the leagues only (built/_links.json): [id, until, clubs?],
 * with the build's day as the first row, ['day', date].
 * @param {CompEntry[]} index
 */
export const linksFile = (index) => {
  const day = indexDay(index);
  return [['day', day], ...index.filter((e) => e.idx && e.promoted && e.until >= day && (e.kind === 'run' || e.clubs || Object.values(SEASON_HUB).includes(e.id))).map((e) => [e.id, e.until, ...(e.clubs ? [e.clubs] : [])])];
};
/** @param {any[][]} rows @returns {CompSlim[]} */
export const unpackLinks = (rows) => {
  const day = rows[0]?.[1] ?? '';
  return rows.slice(1).flatMap(([id, until, clubs]) => {
    const t = BY_ID.get(id);
    return t ? [{ id, title: registryTitle(t), kind: t.run ? 'run' : t.kind, from: '', until, idx: 1, promoted: 1, place: '', price: null, generated: day, ...(clubs ? { clubs } : {}) }] : [];
  });
};

/** The build's own day: the newest `generated` in the index ('' for an empty one). @param {CompEntry[]} index */
export const indexDay = (index) => index.reduce((d, e) => ((e.generated ?? '') > d ? e.generated ?? '' : d), '');

/**
 * Pages worth putting in front of people on `today`: indexable, promoted, running now or starting within `days`.
 * Soonest first (a page already running counts as today), pages with a price before those without, then the ones
 * ending soonest; at most `perKind` of one kind (so a dozen leagues never fill the row) and `max` in all.
 * @param {CompSlim[]} index @param {string} today @param {{ days?: number, max?: number, perKind?: number, group?: string }} [o]
 */
export const liveCompetitions = (/** @type {CompSlim[]} */ index, today, { days = 30, max = 6, perKind = 2, group } = {}) => {
  const horizon = dayAdd(today, days);
  const start = (e) => (e.from > today ? e.from : today);
  const pool = index
    .filter((e) => e.idx && e.promoted && e.until >= today && e.from <= horizon && (!group || e.group === group))
    .sort((a, b) => start(a).localeCompare(start(b)) || Number(b.price !== null) - Number(a.price !== null) || a.until.localeCompare(b.until) || a.id.localeCompare(b.id));
  /** @type {Record<string, number>} */
  const seen = {};
  const out = [];
  for (const e of pool) {
    if ((seen[e.kind] = (seen[e.kind] ?? 0) + 1) > perKind) continue;
    out.push(e);
    if (out.length === max) break;
  }
  return out;
};

/** The pages a menu may list for one group: the same rule, a wider window (a menu is not a news row). @param {CompEntry[]} index @param {'sports' | 'theatre' | 'music'} group */
export const menuCompetitions = (index, group, max = 5) => !index.length ? [] : liveCompetitions(index, indexDay(index), { days: 120, max, perKind: group === 'sports' ? 2 : max, group });

const open = (e, today) => e.idx && e.promoted && e.until >= today;

/**
 * League and season guides a club belongs to: its name is one of the clubs of a football league page, or its
 * league (the SeatGeek kind of its games) has a season guide in this build.
 * @param {CompSlim[]} index @param {string} name @param {string | undefined} kind @param {number} [max]
 */
export const competitionsForClub = (index, name, kind, max = 3) => {
  const today = indexDay(index);
  const id = clubId(name);
  const hub = kind ? SEASON_HUB[/** @type {keyof typeof SEASON_HUB} */ (kind)] : undefined;
  return index.filter((e) => open(e, today) && ((id && e.clubs?.includes(id)) || e.id === hub)).slice(0, max);
};

/**
 * Tour, residency and London-show pages that cover any of these event rows (an act's page, a venue's page): the
 * registry's own membership test, so a page links a run exactly when the run lists that event.
 * @param {CompSlim[]} index @param {{ artist?: string | null, name?: string | null, venue?: string | null, city?: string | null, category?: string | null }[]} rows @param {number} [max]
 */
export const competitionsForRows = (index, rows, max = 3) => {
  const today = indexDay(index);
  const runs = new Map(index.filter((e) => e.kind === 'run' && open(e, today)).map((e) => [e.id, e]));
  if (!runs.size) return [];
  /** @type {CompEntry[]} */
  const out = [];
  for (const t of TOURNAMENTS) {
    const e = runs.get(t.id);
    if (!t.run || !e) continue;
    if (rows.some((r) => runMember(/** @type {any} */ (t.run), r))) out.push(e);
    if (out.length === max) break;
  }
  return out;
};
